"use client";

import { useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";

const DURATION_MS = 1400;

type Parsed = {
  prefix: string;
  suffix: string;
  target: number;
  grouped: boolean;
  decimals: number;
};

/** Splits "30,000+" so only the number counts. */
function parse(value: string): Parsed | null {
  const match = /^(\D*)(\d[\d,]*(?:\.\d+)?)([\s\S]*)$/.exec(value);
  if (!match) return null;
  const [, prefix, digits, suffix] = match;
  const target = Number(digits.replace(/,/g, ""));
  if (!Number.isFinite(target)) return null;
  const dot = digits.indexOf(".");
  return {
    prefix,
    suffix,
    target,
    grouped: digits.includes(","),
    decimals: dot === -1 ? 0 : digits.length - dot - 1,
  };
}

function frameText(n: number, parsed: Parsed): string {
  return (
    parsed.prefix +
    n.toLocaleString("en-IE", {
      minimumFractionDigits: parsed.decimals,
      maximumFractionDigits: parsed.decimals,
      useGrouping: parsed.grouped,
    }) +
    parsed.suffix
  );
}

/** Counts up on scroll into view; `value` is what SSR and reduced motion show. */
export function CountUp({ value, className }: { value: string; className?: string }) {
  const parsed = useMemo(() => parse(value), [value]);
  const rootRef = useRef<HTMLSpanElement>(null);
  const numberRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const node = numberRef.current;
    if (!root || !node || !parsed) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const settle = () => {
      node.textContent = value;
      node.style.fontVariantNumeric = "";
    };

    // Zero it early, or scrolling in shows the real number then snaps back.
    node.textContent = frameText(0, parsed);
    node.style.fontVariantNumeric = "tabular-nums";

    let frame = 0;
    let start: number | null = null;
    const step = (now: number) => {
      start ??= now;
      const t = Math.min(1, (now - start) / DURATION_MS);
      if (t === 1) return settle();
      node.textContent = frameText(parsed.target * (1 - (1 - t) ** 3), parsed);
      frame = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.25 },
    );
    observer.observe(root);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      settle();
    };
  }, [parsed, value]);

  if (!parsed) return <span className={className}>{value}</span>;

  return (
    <span ref={rootRef} className={cn("inline-grid", className)}>
      {/* Holds the final width so shorter frames cannot shift siblings. */}
      <span aria-hidden className="invisible col-start-1 row-start-1">
        {value}
      </span>
      <span ref={numberRef} aria-hidden className="col-start-1 row-start-1">
        {value}
      </span>
      <span className="sr-only">{value}</span>
    </span>
  );
}
